import Autocomplete, { AutocompleteRenderInputParams, createFilterOptions } from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { FilterOptionsState } from '@mui/material/useAutocomplete'
import { SyntheticEvent, useCallback, useMemo } from 'react'

import { useBookingContext } from '@providers/booking'
import { TBookingPet } from '@providers/booking/booking'
import { i18WithParams as t } from '@shared/locale'
import { newPetRecord } from '../../../providers/booking/helper'
import { isCuid } from '@shared/common'

const filter = createFilterOptions<TBookingPet>()

export const AutocompletePetName = ({ groupIdx, petIdx }: { groupIdx: number; petIdx: number }) => {
  const bookingCtx = useBookingContext()
  const group = bookingCtx.state.groups[groupIdx]

  const availablePets = useMemo(() => {
    const petsInProfile = bookingCtx.state.data.petProfiles?.[group.specieId] || []
    const petsInGroup = bookingCtx.state.groups.reduce((acc, g) => {
      g.pets.forEach((p) => acc.add(p.id))
      return acc
    }, new Set())
    // const petsInGroup = new Set(bookingCtx.state.groups.map((g) => g.pets.map(({ id }) => id)).flat())
    return petsInProfile.filter((p) => !petsInGroup.has(p.id))
  }, [bookingCtx.state.data.petProfiles, bookingCtx.state.groups, group.specieId])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const target = event.target as HTMLInputElement
      const newPet = newPetRecord()
      switch (event.key) {
        case 'Tab':
          {
            if (target.value.length > 0) {
              setTimeout(() => {
                newPet.name = target.value
                bookingCtx.dispatch({
                  type: 'SET_PET_SEARCH_FIELD',
                  payload: {
                    groupIdx,
                    petIdx,
                    value: target.value,
                    fieldName: 'name',
                  },
                })
              }, 0)
            }
          }
          break
        default:
          break
      }
    },
    [bookingCtx, groupIdx, petIdx]
  )

  const onChangePetProfile = useCallback(
    (_event: React.SyntheticEvent, newValue: unknown, reason: string, details: unknown) => {
      const basePayload = { groupIdx, petIdx }
      const detailsData = details as GenericSimpleBag

      switch (reason) {
        case 'selectOption': {
          const isNewPet = !isCuid(detailsData.option.id)
          const selectedPet = isNewPet
            ? Object.assign({}, newPetRecord(), { name: detailsData.option.nameInput })
            : Object.assign({}, detailsData.option)
          selectedPet.breedInput = selectedPet.breed
          bookingCtx.dispatch({
            type: 'SET_PET_SEARCH_PROFILE',
            payload: {
              ...basePayload,
              value: selectedPet,
            },
          })
          break
        }
        case 'createOption': {
          const newPet = newPetRecord()
          if (typeof newValue === 'string') {
            newPet.name = newValue
          } else {
            newPet.name = detailsData.option.nameInput
          }
          bookingCtx.dispatch({
            type: 'SET_PET_SEARCH_FIELD',
            payload: {
              ...basePayload,
              value: newPet.name,
              fieldName: 'name',
            },
          })
          break
        }
        case 'clear':
          bookingCtx.dispatch({
            type: 'CLEAR_PET_SEARCH_PROFILE',
            payload: {
              ...basePayload,
            },
          })
          break
        default:
          break
      }
    },
    [bookingCtx, groupIdx, petIdx]
  )

  const onBlur = useCallback(
    (_event: SyntheticEvent) => {
      const target = _event?.target as HTMLInputElement
      if (target.value.length > 0) {
        const newPet = newPetRecord()
        newPet.name = target.value
        bookingCtx.dispatch({
          type: 'SET_PET_SEARCH_FIELD',
          payload: {
            groupIdx,
            petIdx,
            fieldName: 'name',
            value: target.value,
          },
        })
      }
      bookingCtx.dispatch({
        type: 'BLUR_FIELD_VALIDATION',
        payload: {
          fieldName: 'name',
          groupIdx,
          petIdx,
        },
      })
    },
    [bookingCtx, groupIdx, petIdx]
  )

  const doRenderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      params.inputProps.onKeyDown = handleKeyDown
      return (
        <TextField
          {...params}
          label={t('global.label.name')}
          variant="outlined"
          error={Boolean(group.pets[petIdx].nameErrorMsg)}
          helperText={group.pets[petIdx].nameErrorMsg}
        />
      )
    },
    [group.pets, petIdx, handleKeyDown]
  )

  const onFilterOptions = useCallback((options: TBookingPet[], params: FilterOptionsState<TBookingPet>) => {
    const filtered = filter(options, params)
    const { inputValue } = params
    const isExisting = options.some((option) => option.name.toLowerCase() === inputValue.toLowerCase())
    if (inputValue !== '' && !isExisting) {
      filtered.push({
        ...newPetRecord(),
        nameInput: inputValue,
        name: `${t('searchBar.addPet', { petName: inputValue })}`,
      } as TBookingPet)
    }
    return filtered
  }, [])

  const onGetOptionLabel = useCallback((option: string | TBookingPet) => {
    // value selected with enter, right from the input
    if (typeof option === 'string') {
      return option
    }
    if (option.nameInput) {
      return option.nameInput
    }
    return option.name
  }, [])

  const onRenderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: TBookingPet) => <li {...props}>{option.name}</li>,
    []
  )

  const onIsOptionEqualToValue = useCallback((option: TBookingPet, value: TBookingPet) => option.id === value.id, [])

  return (
    <Autocomplete<TBookingPet, false, false, true>
      id="name"
      size="small"
      value={group.pets[petIdx]}
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      freeSolo
      fullWidth
      onChange={onChangePetProfile}
      options={availablePets}
      getOptionLabel={onGetOptionLabel}
      onBlur={onBlur}
      renderOption={onRenderOption}
      renderInput={doRenderInput}
      filterOptions={onFilterOptions}
      isOptionEqualToValue={onIsOptionEqualToValue}
      sx={{ minWidth: `200px` }}
    />
  )
}
